import type { MixerConfig } from '../../config/minigames.config';
import { AttemptGrade } from '../../minigames/MiniGame';
import type { MixerGame } from '../../minigames/MixerGame';
import { COLORS, Cached, drawBurst, drawGradeLabel, roundRect, softShadow, steel } from './paint';

const DEG = Math.PI / 180;
/** What the dial measures per ingredient (MixerConfig.ingredients). */
const MEASURE: Readonly<Record<string, string>> = { Wasser: 'WASSERGEHALT', Zement: 'ZEMENTGEHALT', Kies: 'KIESANTEIL' };
const ICON: Readonly<Record<string, string>> = { Wasser: '💧', Zement: '🧱', Kies: '🪨' };
/** Smallest font (CSS px) – every label readable on a phone (user 2026-10-04: "dass man wirklich jede Schrift lesen kann"). */
const MIN_FONT = 13;
const FONT = '"Segoe UI", Arial, sans-serif';

/**
 * Concrete mixer – only the dosing gauge (user 2026-10-04: the mixer drum left out): the three ingredients with their
 * result on top, below a large dial with what it measures, the scale 0 … 100 %, the green zone and the golden line,
 * the sweeping needle and the readout "IST" / "SOLL".
 */
export class MixerPainter {
  private readonly backdrop = new Cached();

  constructor(private readonly config: MixerConfig) {}

  paint(ctx: CanvasRenderingContext2D, w: number, h: number, game: MixerGame, t: number, scale: number): void {
    ctx.drawImage(this.backdrop.get(w, h, scale, (b) => drawBackdrop(b, w, h)), 0, 0, w, h);
    const chipH = Math.max(28, h * 0.12);
    this.drawChips(ctx, w, chipH, game, t);
    // what is measured and the readout in their own line – nothing overlaps the scale
    const lineY = chipH + 10 + Math.max(24, h * 0.09);
    drawReadout(ctx, w / 2, lineY, Math.max(MIN_FONT + 2, Math.round(h * 0.065)), game, this.config);
    // the dial fills the rest: half circle on its base line
    const top = lineY + Math.max(20, h * 0.06);
    const r = Math.min(w * 0.44, (h - top - 14) * 0.98);
    const cx = w / 2;
    const cy = top + r;
    drawDial(ctx, cx, cy, r, this.config, game);

    const last = game.stops[game.stops.length - 1];
    const age = game.secondsSinceGrade;
    if (last && age < 1.2) {
      if (last.grade === AttemptGrade.Perfect) {
        drawBurst(ctx, { x: cx, y: cy - r * 0.6, count: 30, speed: h * 1.2, gravity: h * 2.2, life: 0.8, colors: ['#fff7c2', COLORS.gold, '#ffb12b'], spread: Math.PI, size: 2, seed: game.stops.length * 131, streaks: true }, age);
      }
      const word = last.angle === null ? 'ZU SPÄT' : last.grade === AttemptGrade.Miss ? 'FALSCH DOSIERT' : undefined;
      drawGradeLabel(ctx, last.grade, age, cx, cy - r * 0.62, Math.max(22, Math.round(r * 0.2)), word);
    }
  }

  private drawChips(ctx: CanvasRenderingContext2D, w: number, chipH: number, game: MixerGame, t: number): void {
    const names = this.config.ingredients;
    const gap = 8;
    const chipW = Math.min(170, (w - 24 - gap * (names.length - 1)) / names.length);
    const x0 = (w - (chipW * names.length + gap * (names.length - 1))) / 2;
    const current = game.finished ? -1 : game.stops.length - (game.pour !== null ? 1 : 0);
    names.forEach((name, i) => {
      const stop = game.stops[i];
      const grade = stop?.grade ?? null;
      const x = x0 + i * (chipW + gap);
      const y = 10;
      const active = i === current && game.pour === null;
      const color = grade === AttemptGrade.Perfect ? COLORS.gold : grade === AttemptGrade.Good ? COLORS.green : grade === AttemptGrade.Miss ? COLORS.red : 'rgba(255,255,255,0.3)';
      ctx.save();
      ctx.fillStyle = active ? `rgba(245, 159, 10, ${0.28 + 0.12 * Math.sin(t * 6)})` : 'rgba(10, 18, 34, 0.85)';
      roundRect(ctx, x, y, chipW, chipH, chipH / 2);
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = active ? COLORS.orange : color;
      ctx.stroke();
      const mark = grade === AttemptGrade.Perfect ? ' ★' : grade === AttemptGrade.Good ? ' ✓' : grade === AttemptGrade.Miss ? ' ✗' : '';
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `700 ${Math.max(MIN_FONT, Math.round(chipH * 0.46))}px ${FONT}`;
      ctx.fillText(`${ICON[name] ?? ''} ${name}${mark}`, x + chipW / 2, y + chipH / 2 + 1, chipW - 12);
      ctx.restore();
    });
  }
}

function drawBackdrop(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  const bg = ctx.createLinearGradient(0, 0, 0, h);
  bg.addColorStop(0, '#1f3054');
  bg.addColorStop(1, '#101a2e');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);
  const light = ctx.createRadialGradient(w / 2, h * 0.65, 0, w / 2, h * 0.65, Math.max(w, h) * 0.7);
  light.addColorStop(0, 'rgba(255, 220, 160, 0.12)');
  light.addColorStop(1, 'rgba(255, 220, 160, 0)');
  ctx.fillStyle = light;
  ctx.fillRect(0, 0, w, h);
}

/** One line over the dial: WASSERGEHALT · IST 62 % · SOLL 48 % (white, red, gold on the dark ground). */
function drawReadout(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, game: MixerGame, c: MixerConfig): void {
  const parts: [string, string][] = [
    [MEASURE[game.ingredient] ?? game.ingredient.toUpperCase(), '#ffffff'],
    [`  IST ${percentOf(game.needle, c.dialDegrees)} %`, '#ff8a7a'],
    [`  SOLL ${percentOf(game.zoneCenter, c.dialDegrees)} %`, COLORS.gold],
  ];
  ctx.save();
  ctx.font = `900 ${size}px ${FONT}`;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  const width = parts.reduce((s, [text]) => s + ctx.measureText(text).width, 0);
  let cx = x - width / 2;
  for (const [text, color] of parts) {
    ctx.fillStyle = color;
    ctx.fillText(text, cx, y);
    cx += ctx.measureText(text).width;
  }
  ctx.restore();
}

/** Needle angle (degrees, −dial/2 … +dial/2) → percent of the scale. */
const percentOf = (deg: number, dial: number): number => Math.round(((deg + dial / 2) / dial) * 100);

function drawDial(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, c: MixerConfig, game: MixerGame): void {
  const half = c.dialDegrees / 2;
  const toAngle = (deg: number) => -Math.PI / 2 + deg * DEG;
  softShadow(ctx, x, y + r * 0.06, r * 1.1, r * 0.1, 0.5);
  // housing and face
  ctx.fillStyle = steel(ctx, x - r * 1.08, x + r * 1.08);
  ctx.beginPath();
  ctx.arc(x, y, r * 1.06, Math.PI, 0);
  ctx.lineTo(x + r * 1.06, y + 4);
  ctx.lineTo(x - r * 1.06, y + 4);
  ctx.closePath();
  ctx.fill();
  const face = ctx.createRadialGradient(x, y - r * 0.4, r * 0.1, x, y, r);
  face.addColorStop(0, '#fffefa');
  face.addColorStop(1, '#e2dfd5');
  ctx.fillStyle = face;
  ctx.beginPath();
  ctx.arc(x, y, r, Math.PI, 0);
  ctx.closePath();
  ctx.fill();

  const band = (from: number, to: number, color: string, radius: number, width: number) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.lineCap = 'butt';
    ctx.beginPath();
    ctx.arc(x, y, radius, toAngle(from), toAngle(to));
    ctx.stroke();
  };
  const bandR = r * 0.84;
  const bandW = r * 0.1;
  band(-half, half, 'rgba(23, 36, 65, 0.12)', bandR, bandW);
  band(-half, -half + 12, 'rgba(224, 71, 60, 0.8)', bandR, bandW);
  band(half - 12, half, 'rgba(224, 71, 60, 0.8)', bandR, bandW);
  const zone = game.zoneCenter;
  const zw = game.zoneHalfWidth;
  band(zone - zw, zone + zw, COLORS.green, bandR, bandW * 1.25);
  band(zone - c.perfectHalfWidth, zone + c.perfectHalfWidth, COLORS.gold, bandR, bandW * 1.6);

  // ticks every 5 %, numbers every 25 %
  ctx.strokeStyle = '#1d2638';
  const numberFont = Math.max(MIN_FONT, Math.round(r * 0.085));
  for (let p = 0; p <= 100; p += 5) {
    const d = -half + (p / 100) * c.dialDegrees;
    const a = toAngle(d);
    const big = p % 25 === 0;
    ctx.lineWidth = big ? 2.5 : 1.2;
    const inner = big ? 0.68 : 0.72;
    ctx.beginPath();
    ctx.moveTo(x + Math.cos(a) * r * inner, y + Math.sin(a) * r * inner);
    ctx.lineTo(x + Math.cos(a) * r * 0.77, y + Math.sin(a) * r * 0.77);
    ctx.stroke();
    if (big) {
      ctx.fillStyle = '#1d2638';
      ctx.font = `700 ${numberFont}px ${FONT}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const nr = r * 0.58;
      ctx.fillText(`${p}`, x + Math.cos(a) * nr, y + Math.sin(a) * nr + (p === 0 || p === 100 ? -numberFont * 0.6 : 0));
    }
  }

  // needle with its shadow
  const a = toAngle(game.needle);
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(a + Math.PI / 2);
  ctx.fillStyle = 'rgba(0,0,0,0.22)';
  ctx.beginPath();
  ctx.moveTo(-r * 0.03 + 3, 3);
  ctx.lineTo(3, -r * 0.9 + 3);
  ctx.lineTo(r * 0.03 + 3, 3);
  ctx.fill();
  ctx.fillStyle = '#d6332a';
  ctx.beginPath();
  ctx.moveTo(-r * 0.03, r * 0.1);
  ctx.lineTo(0, -r * 0.92);
  ctx.lineTo(r * 0.03, r * 0.1);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
  ctx.fillStyle = steel(ctx, x - r * 0.08, x + r * 0.08);
  ctx.beginPath();
  ctx.arc(x, y, r * 0.08, 0, Math.PI * 2);
  ctx.fill();
  // glass highlight
  ctx.fillStyle = 'rgba(255, 255, 255, 0.22)';
  ctx.beginPath();
  ctx.ellipse(x - r * 0.35, y - r * 0.66, r * 0.4, r * 0.09, -0.4, 0, Math.PI * 2);
  ctx.fill();
}
